import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  addCompanyInventorySpec,
  createCompanyInventory,
  deleteCompanyInventory,
  deleteCompanyInventoryMaster,
  fetchCompanyInventory,
  fetchCompanyInventoryItem,
  fetchCompanyInventoryMaster,
  fetchCompanyInventoryMasters,
  updateCompanyInventory,
  updateCompanyInventoryMaster,
} from '../../api/companyInventory';
import { useCompanyStore } from '../../store/companyStore';
import {notifyError, reportError} from '../../utils/notify';
import { formatNumberInput, parseNumberInput } from '../../utils/numberFormat';
import { groupInventoryByMaster } from '../../utils/inventoryGrouping';
import {
  CompanyInventoryItem,
  CompanyInventoryMaster,
  CompanyInventoryPayload,
  formatInventoryQty,
  INVENTORY_UNIT_PRESETS,
  inventoryItemStatusLabel,
  inventoryKindLabel,
  InventoryItemKind,
  InventoryItemStatus,
  isInventoryLowStock,
} from '../../types/companyInventory';
import InventoryStockHistory from './InventoryStockHistory';
import ProductInspectionsTab from './ProductInspectionsTab';
import ListPager from '../common/ListPager';

import '../contract/ContractPanel.css';
import './InventoryPanel.css';

type KindFilter = 'ALL' | InventoryItemKind;
type StatusFilter = 'ALL' | InventoryItemStatus;
type ViewMode = 'list' | 'create' | 'edit' | 'editMaster' | 'detail';

const KIND_OPTIONS: { value: KindFilter; label: string }[] = [
  { value: 'ALL', label: '전체' },
  { value: 'FINISHED', label: '완제품' },
  { value: 'PART', label: '부품' },
  { value: 'MATERIAL', label: '원자재' },
];

const KIND_CARDS: { value: InventoryItemKind; label: string; hint: string }[] = [
  { value: 'PART', label: '부품', hint: '인수검사 입고 대상' },
  { value: 'MATERIAL', label: '원자재', hint: '중간검사 투입 대상' },
  { value: 'FINISHED', label: '완제품', hint: '제품목록 자동 연동' },
];

const SPEC_PAGE_SIZE = 5;

const EMPTY_FORM: CompanyInventoryPayload & {
  stockQtyText: string;
  minStockQtyText: string;
  stockAdjustReason: string;
  originalStockQtyText: string;
} = {
  masterId: null,
  itemKind: 'PART',
  itemStatus: 'ACTIVE',
  itemCode: '',
  itemName: '',
  specText: '',
  makerName: '',
  location: '',
  unit: 'EA',
  remark: '',
  stockQtyText: '0',
  minStockQtyText: '',
  stockAdjustReason: '',
  originalStockQtyText: '0',
};

function kindBadgeClass(kind?: string | null) {
  if (kind === 'FINISHED') return 'is-finished';
  if (kind === 'MATERIAL') return 'is-material';
  return 'is-part';
}

function textOrDash(value?: string | null) {
  const v = value?.trim();
  return v ? v : '-';
}

function InventoryPanel() {
  const companyId = useCompanyStore((state) => state.companyId);
  const [rows, setRows] = useState<CompanyInventoryItem[]>([]);
  const [masters, setMasters] = useState<CompanyInventoryMaster[]>([]);
  const [kindFilter, setKindFilter] = useState<KindFilter>('ALL');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [detail, setDetail] = useState<CompanyInventoryItem | null>(null);
  const [detailMaster, setDetailMaster] = useState<CompanyInventoryMaster | null>(null);
  const [selectedSpecId, setSelectedSpecId] = useState<number | null>(null);
  const [specPage, setSpecPage] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [list, masterList] = await Promise.all([
        fetchCompanyInventory({
          kind: kindFilter,
          keyword,
        }),
        fetchCompanyInventoryMasters({
          kind: kindFilter,
          keyword,
        }),
      ]);
      setRows(list);
      setMasters(masterList);
      setError('');
    } catch {
      reportError(setError, '재고 목록을 불러오지 못했습니다.');
      setRows([]);
      setMasters([]);
    } finally {
      setLoading(false);
    }
  }, [kindFilter, keyword]);

  useEffect(() => {
    setViewMode('list');
    const timer = window.setTimeout(() => void load(), 200);
    return () => window.clearTimeout(timer);
  }, [companyId, load, setViewMode]);

  useEffect(() => {
    if (viewMode !== 'detail' || !detailMaster) return;
    const totalPages = Math.max(1, Math.ceil((detailMaster.items?.length ?? 0) / SPEC_PAGE_SIZE));
    if (specPage > totalPages - 1) setSpecPage(totalPages - 1);
  }, [viewMode, detailMaster, specPage]);

  const filteredRows = useMemo(() => {
    if (statusFilter === 'ALL') return rows;
    return rows.filter((row) => (row.itemStatus || 'ACTIVE') === statusFilter);
  }, [rows, statusFilter]);

  const listMasters = useMemo(() => {
    const groups = groupInventoryByMaster(filteredRows, masters);
    return groups.filter((g) => g.specs.length > 0);
  }, [filteredRows, masters]);

  const counts = useMemo(() => {
    const map = { FINISHED: 0, PART: 0, MATERIAL: 0, low: 0, discontinued: 0 };
    masters.forEach((master) => {
      if (master.itemKind === 'FINISHED') map.FINISHED += 1;
      else if (master.itemKind === 'MATERIAL') map.MATERIAL += 1;
      else map.PART += 1;
    });
    rows.forEach((row) => {
      if (isInventoryLowStock(row) && (row.itemStatus || 'ACTIVE') === 'ACTIVE') map.low += 1;
      if (row.itemStatus === 'DISCONTINUED') map.discontinued += 1;
    });
    return map;
  }, [masters, rows]);

  const patchForm = <K extends keyof typeof EMPTY_FORM>(key: K, value: (typeof EMPTY_FORM)[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const backToList = () => {
    setViewMode('list');
    setEditingId(null);
    setDetail(null);
    setDetailMaster(null);
    setSelectedSpecId(null);
    setSpecPage(0);
    setForm({ ...EMPTY_FORM });
  };

  const openCreate = () => {
    setEditingId(null);
    setDetail(null);
    setDetailMaster(null);
    setSelectedSpecId(null);
    setForm({
      ...EMPTY_FORM,
      itemKind: kindFilter === 'ALL' ? 'PART' : kindFilter,
    });
    setError('');
    setViewMode('create');
  };

  const openCreateSpec = (master: {
    masterId: number | null;
    itemName: string;
    itemKind: string;
    defaultUnit?: string | null;
  }) => {
    setEditingId(null);
    setDetail(null);
    setForm({
      ...EMPTY_FORM,
      masterId: master.masterId,
      itemKind: (master.itemKind as InventoryItemKind) || 'PART',
      itemName: master.itemName,
      unit: master.defaultUnit || 'EA',
    });
    setError('');
    setViewMode('create');
  };

  const fillForm = (row: CompanyInventoryItem) => {
    const stockText = formatInventoryQty(row.stockQty);
    setForm({
      masterId: row.masterId ?? null,
      itemKind: (row.itemKind as InventoryItemKind) || 'PART',
      itemStatus: (row.itemStatus as InventoryItemStatus) || 'ACTIVE',
      itemCode: row.itemCode || '',
      itemName: row.itemName || '',
      specText: row.specText || '',
      makerName: row.makerName || '',
      location: row.location || '',
      unit: row.unit || '',
      remark: row.remark || '',
      stockQtyText: stockText,
      originalStockQtyText: stockText,
      stockAdjustReason: '',
      minStockQtyText:
        row.minStockQty == null || row.minStockQty === ''
          ? ''
          : formatInventoryQty(row.minStockQty),
    });
  };

  const openEdit = (row: CompanyInventoryItem) => {
    setEditingId(row.inventoryId);
    setDetail(row);
    fillForm(row);
    setError('');
    setViewMode('edit');
  };

  const openEditMaster = (master: CompanyInventoryMaster) => {
    setEditingId(null);
    setDetail(null);
    setForm({
      ...EMPTY_FORM,
      masterId: master.masterId,
      itemKind: (master.itemKind as InventoryItemKind) || 'PART',
      itemStatus: (master.itemStatus as InventoryItemStatus) || 'ACTIVE',
      itemName: master.itemName || '',
      makerName: master.makerName || '',
      unit: master.defaultUnit || 'EA',
      remark: master.remark || '',
    });
    setDetailMaster(master);
    setError('');
    setViewMode('editMaster');
  };

  const cancelForm = () => {
    const masterId = form.masterId ?? detailMaster?.masterId ?? null;
    if (
      (viewMode === 'edit' || viewMode === 'editMaster' || viewMode === 'create') &&
      masterId != null
    ) {
      void openMasterDetail(masterId, editingId ?? selectedSpecId);
      return;
    }
    backToList();
  };

  const openMasterDetail = async (masterId: number | null, preferSpecId?: number | null) => {
    if (masterId == null) return;
    setError('');
    setViewMode('detail');
    setSpecPage(0);
    try {
      const master = await fetchCompanyInventoryMaster(masterId);
      setDetailMaster(master);
      const specs = master.items ?? [];
      const preferred =
        (preferSpecId != null
          ? specs.find((s) => s.inventoryId === preferSpecId)
          : null) ??
        specs[0] ??
        null;
      setDetail(preferred);
      setSelectedSpecId(preferred?.inventoryId ?? null);
      setEditingId(preferred?.inventoryId ?? null);
      if (preferred) {
        const idx = specs.findIndex((s) => s.inventoryId === preferred.inventoryId);
        if (idx >= 0) setSpecPage(Math.floor(idx / SPEC_PAGE_SIZE));
      }
    } catch {
      reportError(setError, '품명 상세를 불러오지 못했습니다.');
    }
  };

  const reloadMasterDetail = async (masterId: number, preferSpecId?: number | null) => {
    try {
      const master = await fetchCompanyInventoryMaster(masterId);
      setDetailMaster(master);
      const specs = master.items ?? [];
      const preferred =
        (preferSpecId != null
          ? specs.find((s) => s.inventoryId === preferSpecId)
          : null) ??
        (selectedSpecId != null
          ? specs.find((s) => s.inventoryId === selectedSpecId)
          : null) ??
        specs[0] ??
        null;
      setDetail(preferred);
      setSelectedSpecId(preferred?.inventoryId ?? null);
      if (preferred) {
        const idx = specs.findIndex((s) => s.inventoryId === preferred.inventoryId);
        if (idx >= 0) setSpecPage(Math.floor(idx / SPEC_PAGE_SIZE));
      } else {
        setSpecPage(0);
      }
    } catch {
      // keep current
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.itemName.trim()) {
      notifyError('품명을 입력해 주세요.');
      return;
    }

    if (viewMode === 'editMaster') {
      if (form.masterId == null) {
        notifyError('품명 정보가 없습니다.');
        return;
      }
      try {
        setSaving(true);
        setError('');
        await updateCompanyInventoryMaster(form.masterId, {
          itemKind: form.itemKind,
          itemName: form.itemName.trim(),
          defaultUnit: form.unit?.trim() || undefined,
          makerName: form.makerName?.trim() || '',
          itemStatus: form.itemStatus || 'ACTIVE',
          remark: form.remark?.trim() || '',
        });
        await load();
        await openMasterDetail(form.masterId, selectedSpecId);
      } catch (err) {
        notifyError(
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            '품명 수정에 실패했습니다.'
        );
      } finally {
        setSaving(false);
      }
      return;
    }

    const qty = parseNumberInput(form.stockQtyText);
    if (qty == null || qty < 0) {
      notifyError('현재 재고 수량이 올바르지 않습니다.');
      return;
    }
    const originalQty = parseNumberInput(form.originalStockQtyText) ?? 0;
    const stockChanged =
      editingId != null
        ? originalQty !== qty
        : qty > 0;
    if (stockChanged && !form.stockAdjustReason.trim()) {
      notifyError(
        editingId
          ? '재고 수량을 변경할 때는 사유를 입력해 주세요.'
          : '초기 재고를 등록할 때는 사유를 입력해 주세요. (예: 초기 재고 등록)'
      );
      return;
    }
    let minQty: number | null = null;
    if (form.minStockQtyText.trim()) {
      minQty = parseNumberInput(form.minStockQtyText);
      if (minQty == null || minQty < 0) {
        notifyError('안전재고 수량이 올바르지 않습니다.');
        return;
      }
    }
    try {
      setSaving(true);
      setError('');
      const payload: CompanyInventoryPayload = {
        masterId: form.masterId ?? undefined,
        itemKind: form.itemKind,
        itemStatus: form.itemStatus || 'ACTIVE',
        itemCode: form.itemCode?.trim() || undefined,
        itemName: form.itemName.trim(),
        specText: form.specText?.trim() || '',
        makerName: form.makerName?.trim() || undefined,
        location: form.location?.trim() || undefined,
        stockQty: qty,
        minStockQty: minQty,
        unit: form.unit?.trim() || undefined,
        remark: form.remark?.trim() || undefined,
        ...(stockChanged
          ? { stockAdjustReason: form.stockAdjustReason.trim() }
          : {}),
      };
      let inventoryId = editingId;
      if (editingId) {
        await updateCompanyInventory(editingId, {
          ...payload,
          itemCode: payload.itemCode ?? '',
          makerName: payload.makerName ?? '',
          location: payload.location ?? '',
          unit: payload.unit ?? '',
          remark: payload.remark ?? '',
          minStockQty: minQty ?? 0,
        });
      } else if (form.masterId) {
        const created = await addCompanyInventorySpec(form.masterId, payload);
        inventoryId = created.inventoryId;
      } else {
        const created = await createCompanyInventory(payload);
        inventoryId = created.inventoryId;
      }
      await load();
      if (inventoryId) {
        try {
          const next = await fetchCompanyInventoryItem(inventoryId);
          const masterId = next.masterId ?? form.masterId;
          if (masterId != null) {
            setForm({ ...EMPTY_FORM });
            await openMasterDetail(masterId, next.inventoryId);
            return;
          }
          setDetail(next);
          setEditingId(next.inventoryId);
          setViewMode('detail');
          setForm({ ...EMPTY_FORM });
          return;
        } catch {
          // fall through
        }
      }
      backToList();
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '재고 저장에 실패했습니다.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (row: CompanyInventoryItem) => {
    const nextStatus: InventoryItemStatus =
      row.itemStatus === 'DISCONTINUED' ? 'ACTIVE' : 'DISCONTINUED';
    const label = nextStatus === 'DISCONTINUED' ? '단종' : '사용중';
    if (
      !window.confirm(
        `「${row.itemName}」(${row.specText?.trim() || '기본'})을(를) ${label}(으)로 변경할까요?`
      )
    )
      return;
    try {
      await updateCompanyInventory(row.inventoryId, { itemStatus: nextStatus });
      await load();
      if (detailMaster?.masterId) {
        await reloadMasterDetail(detailMaster.masterId, row.inventoryId);
      }
    } catch {
      notifyError('상태 변경에 실패했습니다.');
    }
  };

  const handleDelete = async (row: CompanyInventoryItem) => {
    if (
      !window.confirm(
        `「${row.itemName}」 규격 「${row.specText?.trim() || '기본'}」을(를) 삭제할까요?\n\n이 규격에 연결된 인수·중간·제품검사, NCR, 발주 품목, 재고이력이 함께 삭제됩니다.`
      )
    )
      return;
    try {
      await deleteCompanyInventory(row.inventoryId);
      await load();
      if (detailMaster?.masterId) {
        const master = await fetchCompanyInventoryMaster(detailMaster.masterId);
        if (!(master.items && master.items.length > 0)) {
          backToList();
          return;
        }
        await reloadMasterDetail(detailMaster.masterId);
      } else {
        backToList();
      }
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '재고 삭제에 실패했습니다.'
      );
    }
  };

  const handleDeleteMaster = async (master: CompanyInventoryMaster) => {
    const specCount = master.items?.length ?? 0;
    if (
      !window.confirm(
        `품명 「${master.itemName}」과(와) 하위 규격 ${specCount}건을 모두 삭제할까요?\n\n연결된 검사·NCR·발주 품목·재고이력이 함께 삭제됩니다. 이 작업은 되돌릴 수 없습니다.`
      )
    )
      return;
    try {
      await deleteCompanyInventoryMaster(master.masterId);
      await load();
      backToList();
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '품명 삭제에 실패했습니다.'
      );
    }
  };

  const handleToggleMasterStatus = async (master: CompanyInventoryMaster) => {
    const nextStatus: InventoryItemStatus =
      master.itemStatus === 'DISCONTINUED' ? 'ACTIVE' : 'DISCONTINUED';
    const label = nextStatus === 'DISCONTINUED' ? '단종' : '사용중';
    if (
      !window.confirm(
        `품명 「${master.itemName}」을(를) ${label}(으)로 변경할까요? (하위 규격 포함)`
      )
    )
      return;
    try {
      await updateCompanyInventoryMaster(master.masterId, { itemStatus: nextStatus });
      await load();
      await reloadMasterDetail(master.masterId, selectedSpecId);
    } catch {
      notifyError('상태 변경에 실패했습니다.');
    }
  };

  const renderDetail = () => {
    if (!detailMaster) return null;
    const specs = detailMaster.items ?? [];
    const activeSpec =
      (selectedSpecId != null
        ? specs.find((s) => s.inventoryId === selectedSpecId)
        : null) ??
      detail ??
      specs[0] ??
      null;
    const masterDiscontinued = detailMaster.itemStatus === 'DISCONTINUED';
    const totalStock = specs.reduce((sum, s) => sum + (Number(s.stockQty) || 0), 0);
    const anyLow = specs.some((s) => isInventoryLowStock(s));
    const totalPages = Math.max(1, Math.ceil(specs.length / SPEC_PAGE_SIZE));
    const safePage = Math.min(specPage, totalPages - 1);
    const pageStart = safePage * SPEC_PAGE_SIZE;
    const pageSpecs = specs.slice(pageStart, pageStart + SPEC_PAGE_SIZE);

    return (
      <div className="inv-detail">
        <div className="inv-detail__hero">
          <div>
            <p className="inv-detail__eyebrow">품명 마스터</p>
            <h3>{detailMaster.itemName}</h3>
            <p className="inv-detail__summary">
              규격 {specs.length}단 · 합계 재고{' '}
              <strong>
                {formatInventoryQty(totalStock)}
                {detailMaster.defaultUnit ? ` ${detailMaster.defaultUnit}` : ''}
              </strong>
            </p>
            <div className="inv-detail__badges">
              <span className={`inv-badge ${kindBadgeClass(detailMaster.itemKind)}`}>
                {inventoryKindLabel(detailMaster.itemKind)}
              </span>
              <span className={`inv-badge ${masterDiscontinued ? 'is-discontinued' : 'is-ok'}`}>
                {inventoryItemStatusLabel(detailMaster.itemStatus)}
              </span>
              {anyLow && !masterDiscontinued ? (
                <span className="inv-badge is-low">안전재고 부족</span>
              ) : null}
            </div>
          </div>
          <div className="inv-detail__actions">
            <button
              type="button"
              className="is-ghost"
              onClick={() => void handleToggleMasterStatus(detailMaster)}
            >
              {masterDiscontinued ? '사용중으로' : '단종으로'}
            </button>
            <button type="button" className="is-ghost" onClick={() => openEditMaster(detailMaster)}>
              수정
            </button>
            <button
              type="button"
              className="is-danger"
              onClick={() => void handleDeleteMaster(detailMaster)}
            >
              삭제
            </button>
            <button
              type="button"
              className="is-primary"
              onClick={() =>
                openCreateSpec({
                  masterId: detailMaster.masterId,
                  itemName: detailMaster.itemName,
                  itemKind: String(detailMaster.itemKind),
                  defaultUnit: detailMaster.defaultUnit,
                })
              }
            >
              규격 추가
            </button>
          </div>
        </div>

        <div className="inv-detail__body">
          <section className="inv-detail__section inv-detail__section--panel">
            <div className="inv-detail__section-head">
              <h4>기본 정보</h4>
            </div>
            <dl className="inv-detail__grid">
              <div className="inv-detail__item is-span-2">
                <dt>품명</dt>
                <dd>{detailMaster.itemName}</dd>
              </div>
              <div className="inv-detail__item is-span-2">
                <dt>제조사</dt>
                <dd className={detailMaster.makerName ? undefined : 'is-muted'}>
                  {textOrDash(detailMaster.makerName)}
                </dd>
              </div>
              <div className="inv-detail__item">
                <dt>기본 단위</dt>
                <dd className={detailMaster.defaultUnit ? undefined : 'is-muted'}>
                  {textOrDash(detailMaster.defaultUnit)}
                </dd>
              </div>
              <div className="inv-detail__item is-span-2">
                <dt>메모</dt>
                <dd className={detailMaster.remark ? undefined : 'is-muted'}>
                  {textOrDash(detailMaster.remark)}
                </dd>
              </div>
            </dl>
          </section>

          <section className="inv-detail__section inv-detail__section--panel">
            <div className="inv-detail__section-head">
              <h4>규격별 재고</h4>
              <span>
                {specs.length === 0
                  ? '0단'
                  : `${pageStart + 1}–${Math.min(pageStart + pageSpecs.length, specs.length)} / ${specs.length}단`}
              </span>
            </div>
            {specs.length === 0 ? (
              <div className="inv-spec-empty">
                <p>등록된 규격이 없습니다.</p>
                <button
                  type="button"
                  className="is-primary"
                  onClick={() =>
                    openCreateSpec({
                      masterId: detailMaster.masterId,
                      itemName: detailMaster.itemName,
                      itemKind: String(detailMaster.itemKind),
                      defaultUnit: detailMaster.defaultUnit,
                    })
                  }
                >
                  첫 규격 추가
                </button>
              </div>
            ) : (
              <>
                <div className="inv-spec-list">
                  {pageSpecs.map((spec, offset) => {
                    const index = pageStart + offset;
                    const low = isInventoryLowStock(spec);
                    const discontinued = spec.itemStatus === 'DISCONTINUED';
                    const selected = activeSpec?.inventoryId === spec.inventoryId;
                    return (
                      <article
                        key={spec.inventoryId}
                        className={`inv-spec-card${selected ? ' is-selected' : ''}${
                          discontinued ? ' is-discontinued' : ''
                        }${low && !discontinued ? ' is-low' : ''}`}
                        onClick={() => {
                          setSelectedSpecId(spec.inventoryId);
                          setDetail(spec);
                        }}
                      >
                        <div className="inv-spec-card__index" aria-hidden>
                          {index + 1}
                        </div>
                        <div className="inv-spec-card__main">
                          <div className="inv-spec-card__head">
                            <div>
                              <strong>{spec.specText?.trim() || '기본'}</strong>
                              {spec.itemCode?.trim() ? (
                                <span className="inv-spec-card__code">{spec.itemCode}</span>
                              ) : null}
                            </div>
                            <span
                              className={`inv-badge ${discontinued ? 'is-discontinued' : 'is-ok'}`}
                            >
                              {inventoryItemStatusLabel(spec.itemStatus)}
                            </span>
                          </div>
                          <div className="inv-spec-card__stats">
                            <div className="inv-spec-card__stat is-stock">
                              <span>현재고</span>
                              <strong className={low && !discontinued ? 'is-low' : undefined}>
                                {formatInventoryQty(spec.stockQty)}
                                {spec.unit ? (
                                  <em>{spec.unit}</em>
                                ) : null}
                              </strong>
                            </div>
                            <div className="inv-spec-card__stat">
                              <span>안전재고</span>
                              <strong>
                                {spec.minStockQty == null || spec.minStockQty === ''
                                  ? '-'
                                  : formatInventoryQty(spec.minStockQty)}
                              </strong>
                            </div>
                            <div className="inv-spec-card__stat">
                              <span>위치</span>
                              <strong>{textOrDash(spec.location)}</strong>
                            </div>
                            <div className="inv-spec-card__stat">
                              <span>제조사</span>
                              <strong>{textOrDash(spec.makerName)}</strong>
                            </div>
                          </div>
                          {spec.remark ? (
                            <p className="inv-spec-card__remark">{spec.remark}</p>
                          ) : null}
                          <div className="inv-spec-card__actions">
                            <button
                              type="button"
                              className="is-ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleToggleStatus(spec);
                              }}
                            >
                              {discontinued ? '사용중' : '단종'}
                            </button>
                            <button
                              type="button"
                              className="is-ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEdit(spec);
                              }}
                            >
                              수정
                            </button>
                            <button
                              type="button"
                              className="is-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleDelete(spec);
                              }}
                            >
                              삭제
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
                {specs.length > SPEC_PAGE_SIZE ? (
                  <ListPager
                    page={safePage}
                    totalPages={totalPages}
                    totalElements={specs.length}
                    onChange={setSpecPage}
                  />
                ) : null}
              </>
            )}
          </section>

          {activeSpec ? (
            <>
              <section className="inv-detail__section inv-detail__section--panel inv-detail__section--history">
                <div className="inv-detail__section-head">
                  <h4>검사 · 이력</h4>
                  <span>{activeSpec.specText?.trim() || '기본'}</span>
                </div>
                <ProductInspectionsTab
                  inventoryId={activeSpec.inventoryId}
                  itemKind={activeSpec.itemKind}
                />
              </section>
              <section className="inv-detail__section inv-detail__section--panel inv-detail__section--history">
                <InventoryStockHistory
                  inventoryId={activeSpec.inventoryId}
                  unit={activeSpec.unit}
                />
              </section>
            </>
          ) : null}
        </div>
      </div>
    );
  };

  const toolbarTitle =
    viewMode === 'create'
      ? '재고 등록'
      : viewMode === 'edit'
        ? '재고 수정'
        : viewMode === 'editMaster'
          ? '품명 수정'
          : viewMode === 'detail'
            ? '재고 상세'
            : '재고관리';

  const toolbarDesc =
    viewMode === 'list'
      ? '품명 단위로 표시됩니다. 상세에서 규격별 재고를 관리합니다.'
      : viewMode === 'detail'
        ? '품명 상세 · 규격별 재고를 여러 단으로 확인합니다.'
        : viewMode === 'editMaster'
          ? '유형(부품/원자재/완제품)·품명 등 마스터 정보를 수정합니다. 유형 변경은 하위 규격 전체에 적용됩니다.'
          : form.masterId
            ? '선택한 품명 아래에 규격(SKU)을 추가합니다.'
            : '품명·규격을 입력하면 품명 마스터가 자동으로 맞춰집니다. 같은 품명이면 규격만 추가됩니다.';

  return (
    <main className="contract-panel">
      <aside className="contract-panel__sidebar">
        <div className="contract-panel__side-header">
          <strong>재고관리</strong>
          <span>완제품 · 부품 · 원자재</span>
        </div>
        <div className="contract-panel__search">
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="품명 / 품번 / 규격 / 위치 검색"
          />
        </div>
        <div className="contract-panel__list-wrap">
          <ul className="contract-panel__list">
            {KIND_OPTIONS.map((opt) => (
              <li key={opt.value}>
                <button
                  type="button"
                  className={`contract-panel__list-item${
                    kindFilter === opt.value ? ' is-active' : ''
                  }`}
                  onClick={() => setKindFilter(opt.value)}
                >
                  <span className="contract-panel__list-name">{opt.label}</span>
                  <span className="contract-panel__list-meta">
                    {opt.value === 'ALL'
                      ? `${masters.length}건`
                      : `${counts[opt.value as InventoryItemKind] ?? 0}건`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <div className="inv-status-filters">
            {(
              [
                { value: 'ALL', label: '전체상태' },
                { value: 'ACTIVE', label: '사용중' },
                { value: 'DISCONTINUED', label: '단종' },
              ] as const
            ).map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={statusFilter === opt.value ? 'is-active' : undefined}
                onClick={() => setStatusFilter(opt.value)}
              >
                {opt.label}
                {opt.value === 'DISCONTINUED' && counts.discontinued > 0
                  ? ` ${counts.discontinued}`
                  : ''}
              </button>
            ))}
          </div>
          {counts.low > 0 ? (
            <p className="contract-panel__hint" style={{ padding: '8px 12px', color: '#c62828' }}>
              안전재고 부족 {counts.low}건
            </p>
          ) : null}
        </div>
      </aside>

      <section className="contract-panel__main inv-panel__main">
        <div className="contract-panel__toolbar">
          <div>
            <h2>{toolbarTitle}</h2>
            <p>{toolbarDesc}</p>
          </div>
          <div className="contract-panel__toolbar-actions">
            {viewMode !== 'list' ? (
              <button type="button" className="contract-panel__ghost" onClick={backToList}>
                목록으로
              </button>
            ) : null}
            {viewMode === 'list' ? (
              <button type="button" className="contract-panel__primary" onClick={openCreate}>
                재고 등록
              </button>
            ) : null}
          </div>
        </div>

        <div className="inv-panel__scroll">
          {error ? <p className="contract-panel__error">{error}</p> : null}

          {viewMode === 'create' || viewMode === 'edit' || viewMode === 'editMaster' ? (
            <form className="inv-form" onSubmit={(e) => void handleSubmit(e)}>
              <div className="inv-form__header">
                <div>
                  <h3>
                    {viewMode === 'editMaster'
                      ? '품명 수정'
                      : viewMode === 'edit'
                        ? '규격 수정'
                        : form.masterId
                          ? '규격 추가'
                          : '재고 등록'}
                  </h3>
                  <p>
                    {viewMode === 'editMaster'
                      ? '유형·품명·제조사 등 마스터 정보를 수정합니다. 유형 변경은 이 품명의 모든 규격에 적용됩니다.'
                      : viewMode === 'edit'
                        ? '규격별 재고·위치 등을 수정합니다. 유형을 바꾸면 같은 품명의 규격 전체에 적용됩니다.'
                        : form.masterId
                          ? '기존 품명 아래에 규격을 추가합니다.'
                          : '품명과 규격을 입력하세요. 같은 품명이면 규격만 추가됩니다.'}
                  </p>
                </div>
              </div>
              <div className="inv-form__body">
                <section className="inv-form__section">
                  <div className="inv-form__section-head">
                    <strong>유형 · 상태</strong>
                    <span>검사·재고 흐름을 구분합니다</span>
                  </div>
                  <div className="inv-form__grid">
                    <label className="inv-form__field is-span-4">
                      <span>유형</span>
                      <div className="inv-kind-cards">
                        {KIND_CARDS.map((card) => (
                          <button
                            key={card.value}
                            type="button"
                            className={`inv-kind-card${
                              form.itemKind === card.value ? ' is-active' : ''
                            }`}
                            disabled={!!form.masterId && viewMode === 'create'}
                            onClick={() => patchForm('itemKind', card.value)}
                          >
                            <strong>{card.label}</strong>
                            <span>{card.hint}</span>
                          </button>
                        ))}
                      </div>
                      {viewMode === 'edit' || viewMode === 'editMaster' ? (
                        <span className="inv-form__hint">
                          부품 ↔ 원자재 ↔ 완제품으로 변경할 수 있습니다. 같은 이름·유형 조합이 이미
                          있으면 저장되지 않습니다.
                        </span>
                      ) : null}
                    </label>
                    <label className="inv-form__field">
                      <span>취급 상태</span>
                      <select
                        value={form.itemStatus || 'ACTIVE'}
                        onChange={(e) =>
                          patchForm('itemStatus', e.target.value as InventoryItemStatus)
                        }
                      >
                        <option value="ACTIVE">사용중</option>
                        <option value="DISCONTINUED">단종</option>
                      </select>
                    </label>
                  </div>
                </section>

                <section className="inv-form__section">
                  <div className="inv-form__section-head">
                    <strong>
                      {viewMode === 'editMaster' ? '품명 · 제조사' : '품번 · 품명 · 규격'}
                    </strong>
                    <span>
                      {viewMode === 'editMaster'
                        ? '품명 마스터 공통 정보'
                        : '같은 품명이면 규격만 추가됩니다'}
                    </span>
                  </div>
                  <div className="inv-form__grid">
                    {viewMode !== 'editMaster' ? (
                      <label className="inv-form__field">
                        <span>품번</span>
                        <input
                          value={form.itemCode}
                          onChange={(e) => patchForm('itemCode', e.target.value)}
                          placeholder="자동 또는 직접 입력"
                        />
                      </label>
                    ) : null}
                    <label className="inv-form__field is-span-2">
                      <span>
                        품명<em>*</em>
                      </span>
                      <input
                        value={form.itemName}
                        onChange={(e) => patchForm('itemName', e.target.value)}
                        placeholder="예: 스텐베이스커버"
                        required
                        readOnly={!!form.masterId && viewMode === 'create'}
                      />
                      {form.masterId && viewMode === 'create' ? (
                        <span className="inv-form__hint">기존 품명에 규격만 추가합니다.</span>
                      ) : null}
                    </label>
                    {viewMode !== 'editMaster' ? (
                      <label className="inv-form__field">
                        <span>규격</span>
                        <input
                          value={form.specText}
                          onChange={(e) => patchForm('specText', e.target.value)}
                          placeholder="예: Ø50 / t1.2"
                        />
                      </label>
                    ) : null}
                    <label className="inv-form__field">
                      <span>제조사</span>
                      <input
                        value={form.makerName}
                        onChange={(e) => patchForm('makerName', e.target.value)}
                      />
                    </label>
                    {viewMode !== 'editMaster' ? (
                      <label className="inv-form__field">
                        <span>보관 위치</span>
                        <input
                          value={form.location}
                          onChange={(e) => patchForm('location', e.target.value)}
                          placeholder="창고 / 선반"
                        />
                      </label>
                    ) : null}
                  </div>
                </section>

                {viewMode === 'editMaster' ? (
                  <section className="inv-form__section">
                    <div className="inv-form__section-head">
                      <strong>기본 단위 · 메모</strong>
                      <span>하위 규격의 기본값으로 쓰입니다</span>
                    </div>
                    <div className="inv-form__grid">
                      <label className="inv-form__field is-span-2">
                        <span>기본 단위</span>
                        <input
                          value={form.unit}
                          onChange={(e) => patchForm('unit', e.target.value)}
                          placeholder="EA / kg / m"
                        />
                        <div className="inv-unit-chips">
                          {INVENTORY_UNIT_PRESETS.map((unit) => (
                            <button
                              key={unit}
                              type="button"
                              className={form.unit === unit ? 'is-active' : undefined}
                              onClick={() => patchForm('unit', unit)}
                            >
                              {unit}
                            </button>
                          ))}
                        </div>
                      </label>
                      <label className="inv-form__field is-span-2">
                        <span>비고</span>
                        <textarea
                          value={form.remark}
                          onChange={(e) => patchForm('remark', e.target.value)}
                          placeholder="납품처, 품질등급, 대체품 등"
                        />
                      </label>
                    </div>
                  </section>
                ) : (
                  <section className="inv-form__section">
                    <div className="inv-form__section-head">
                      <strong>수량 · 단위</strong>
                      <span>현재고 변경 시 사유가 필요합니다</span>
                    </div>
                    <div className="inv-form__grid">
                      <label className="inv-form__field">
                        <span>
                          현재 재고<em>*</em>
                        </span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={form.stockQtyText}
                          onChange={(e) =>
                            patchForm('stockQtyText', formatNumberInput(e.target.value))
                          }
                        />
                      </label>
                      <label className="inv-form__field">
                        <span>안전재고</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={form.minStockQtyText}
                          onChange={(e) =>
                            patchForm('minStockQtyText', formatNumberInput(e.target.value))
                          }
                          placeholder="미입력 시 미사용"
                        />
                      </label>
                      {(viewMode === 'create' && (parseNumberInput(form.stockQtyText) ?? 0) > 0) ||
                      (viewMode === 'edit' &&
                        (parseNumberInput(form.stockQtyText) ?? 0) !==
                          (parseNumberInput(form.originalStockQtyText) ?? 0)) ? (
                        <label className="inv-form__field is-span-2">
                          <span>
                            재고 변경 사유<em>*</em>
                          </span>
                          <input
                            value={form.stockAdjustReason}
                            onChange={(e) => patchForm('stockAdjustReason', e.target.value)}
                            maxLength={500}
                            placeholder="예: 실사 조정, 초기 재고 등록"
                          />
                        </label>
                      ) : null}
                      <label className="inv-form__field is-span-2">
                        <span>단위</span>
                        <input
                          value={form.unit}
                          onChange={(e) => patchForm('unit', e.target.value)}
                          placeholder="EA / kg / m"
                        />
                        <div className="inv-unit-chips">
                          {INVENTORY_UNIT_PRESETS.map((unit) => (
                            <button
                              key={unit}
                              type="button"
                              className={form.unit === unit ? 'is-active' : undefined}
                              onClick={() => patchForm('unit', unit)}
                            >
                              {unit}
                            </button>
                          ))}
                        </div>
                      </label>
                      <label className="inv-form__field is-span-2">
                        <span>비고</span>
                        <textarea
                          value={form.remark}
                          onChange={(e) => patchForm('remark', e.target.value)}
                          placeholder="납품처, 품질등급, 대체품 등"
                        />
                      </label>
                    </div>
                  </section>
                )}

                <div className="inv-form__actions">
                  <button type="button" className="is-ghost" onClick={cancelForm}>
                    취소
                  </button>
                  <button type="submit" className="is-primary" disabled={saving}>
                    {saving
                      ? '저장 중...'
                      : viewMode === 'edit' || viewMode === 'editMaster'
                        ? '수정 저장'
                        : '등록'}
                  </button>
                </div>
              </div>
            </form>
          ) : null}

          {viewMode === 'detail' ? renderDetail() : null}

          {viewMode === 'list' ? (
            <div className="inv-card-list-wrap">
              {loading ? (
                <p className="contract-panel__hint">불러오는 중...</p>
              ) : listMasters.length === 0 ? (
                <p className="contract-panel__hint">
                  {statusFilter !== 'ALL'
                    ? '조건에 맞는 재고가 없습니다.'
                    : '등록된 재고가 없습니다. 「재고 등록」으로 추가하세요.'}
                </p>
              ) : (
                <div className="inv-card-list">
                  {listMasters.map((group) => {
                    const totalStock = group.specs.reduce(
                      (sum, s) => sum + (Number(s.stockQty) || 0),
                      0
                    );
                    const anyLow = group.specs.some((s) => isInventoryLowStock(s));
                    const allDisc = group.specs.every((s) => s.itemStatus === 'DISCONTINUED');
                    const unit =
                      group.specs.find((s) => s.unit?.trim())?.unit ||
                      masters.find((m) => m.masterId === group.masterId)?.defaultUnit ||
                      '';
                    const maker =
                      group.specs.find((s) => s.makerName?.trim())?.makerName ||
                      masters.find((m) => m.masterId === group.masterId)?.makerName ||
                      '';
                    return (
                      <article
                        key={
                          group.masterId != null
                            ? `m-${group.masterId}`
                            : `n-${group.itemKind}|${group.itemName}`
                        }
                        className={[
                          'inv-card',
                          'inv-card--compact',
                          allDisc ? 'is-discontinued' : '',
                          anyLow && !allDisc ? 'is-low' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onDoubleClick={() => void openMasterDetail(group.masterId)}
                      >
                        <div className="inv-card__main">
                          <div className="inv-card__identity">
                            <div className="inv-card__badges">
                              <span className={`inv-badge ${kindBadgeClass(group.itemKind)}`}>
                                {inventoryKindLabel(group.itemKind)}
                              </span>
                              <span
                                className={`inv-badge ${allDisc ? 'is-discontinued' : 'is-ok'}`}
                              >
                                {allDisc ? '단종' : '사용중'}
                              </span>
                              {anyLow && !allDisc ? (
                                <span className="inv-badge is-low">부족</span>
                              ) : null}
                            </div>
                            <h3 className="inv-card__title">{group.itemName}</h3>
                            <p className="inv-card__sub">
                              {[maker || null, `규격 ${group.specs.length}단`]
                                .filter(Boolean)
                                .join(' · ')}
                            </p>
                          </div>
                          <div className="inv-card__stats">
                            <div className="inv-card__stat">
                              <span>합계 재고</span>
                              <strong className={anyLow && !allDisc ? 'is-low' : undefined}>
                                {formatInventoryQty(totalStock)}
                                {unit ? ` ${unit}` : ''}
                              </strong>
                            </div>
                            <div className="inv-card__stat">
                              <span>단위</span>
                              <strong>{unit || '-'}</strong>
                            </div>
                          </div>
                          <div className="inv-card__actions">
                            <button
                              type="button"
                              className="is-ghost"
                              onClick={() => void openMasterDetail(group.masterId)}
                              disabled={group.masterId == null}
                            >
                              상세
                            </button>
                            {group.masterId != null ? (
                              <button
                                type="button"
                                className="is-ghost"
                                onClick={() =>
                                  openCreateSpec({
                                    masterId: group.masterId,
                                    itemName: group.itemName,
                                    itemKind: group.itemKind,
                                  })
                                }
                              >
                                규격 추가
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </section>
    </main>
  );
}

export default InventoryPanel;
